import { makeAutoObservable, runInAction } from "mobx";
import http from "../services/http";
import AppConsts from "../libs/AppConsts";
import FEATURES from "../constants/features";

type NavigateFunction = (path: string) => void;

class AppConfigStore {
  config?: IConfig;
  isLoading = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isWelcomeMessage() {
    return this.isOwner && !!this.config
      && !this.config.readSystemNotifications.includes("welcome_message");
  }

  // get isLicenseActive() {
  //   if (!this.config?.license) return false;

  //   const now = dayjs();
  //   const startsAt = dayjs(this.config.license.startsAt);
  //   const expiresAt = dayjs(this.config.license.expiresAt);

  //   return now.isAfter(startsAt) && now.isBefore(expiresAt);
  // }

  // get isLicenseNotStarted() {
  //   return this.config?.license && dayjs().isBefore(dayjs(this.config.license.startsAt));
  // }

  // get isTrialTariff() {
  //   return this.config?.license?.tariff?.systemIdentifier === "trial_period";
  // }

  get currency() {
    return this.config?.currency.title;
  }

  get isOwner() {
    return this.config?.role.name === "owner";
  }

  get isInstaller() {
    return this.config?.role.name === "installer";
  }

  get isSuperAdmin() {
    return this.config?.role.name === AppConsts.hostAdmin;
  }

  get menuPaths(): string[] {
    const paths = (items: IMenuItem[]): string[] => items.flatMap((item) => [
      item.route.url.replace(/^\/+|\/+$/g, ""),
      ...paths(item.children ?? []),
    ]);
    return paths(this.config?.menuItems ?? []).filter(Boolean);
  }

  canAccessPath(path: string): boolean {
    const normalized = path.replace(/^\/+|\/+$/g, "");
    // Support is available to company roles, independently of purchased features.
    if (normalized === "support") return !!this.config && !this.isSuperAdmin;
    return this.menuPaths.some((allowed) => normalized === allowed || normalized.startsWith(`${allowed}/`));
  }

  async getConfig() {
    runInAction(() => { this.isLoading = true });
    try {
      const result = await http.get("/app-config");
      runInAction(() => {
        this.config = result.data;
      });
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async onRefreshConfig() {
    const result = await http.get("/app-config");
    runInAction(() => {
      this.config = result.data;
    });
  }

  async onCompleteWelcome(navigate: NavigateFunction) {
    try {
      await http.post("/user/mark-notification-read", { notificationSystemIdentifier: "welcome_message" });

      await this.getConfig();
      navigate(this.canAccessPath("settings/basic") ? "/settings/basic" : "/");
    } catch (error) {
      console.error(error);
    }
  }

  get hasFeature() {
    return (code: string): boolean => {
      return !!this.config?.features?.[code];
    };
  }

  get getFeature() {
    return (code: string): any => {
      return this.config?.features?.[code];
    };
  }

  get canExportCommercialOffer() {
    return this.hasFeature(FEATURES.COMMERCIAL_OFFER_EXPORT);
  }
}

const appConfigStore = new AppConfigStore();
export default appConfigStore;

interface IConfig {
  currency: {
    value: string;
    title: string;
  };
  language: string;
  role: {
    name: string;
    label: string;
  };
  menuItems: IMenuItem[];
  license?: {
    id: string;
    startsAt: Date;
    tariff: ITariff;
    expiresAt: Date;
    createdAt: Date;
  };
  storage: IStorage;
  fcmTokens: string[];
  readSystemNotifications: string[];
  features: Record<string, boolean|number>;
}

interface IStorage {
  used: number;
  limit: number;
}

interface ITariff {
  id: string;
  name: string;
  systemIdentifier?: string;
  limits: ILimit[];
  policies: IPolicy[];
  createdAt: Date;
}

interface ILimit {
  id: string;
  key: {
    value: string;
    title: string;
  };
  value: number;
  createdAt: Date;
}

interface IPolicy {
  id: string;
  name: string;
  value: number;
  description: string;
  routes: IRoute[];
}

interface IRoute {
  id: string;
  name: string;
  url: string;
}

export interface IMenuItem {
  name: string;
  icon: string;
  children?: IMenuItem[];
  route: {
    name: string;
    url: string;
  };
}
